import React, { useMemo } from 'react';
import { useOfficialData } from '@hooks';
import { filterRoleList } from '@utils/dashboardApi';
import './OfficialGrid.css';

const OfficialGrid = ({
                        roleList = [],
                        roleEmpInfo = [],
                        data = [],
                        siteCodes = [],
                        supervMappings = [],
                        sizeCategory,
                        selectedSite = "",
                        compCode = "",
                      }) => {
  // 사업장별 데이터 조회 (사업장이 선택되었을 때만)
  const { data: siteData } = useOfficialData(
      selectedSite ? { compCode: compCode, siteCode: parseInt(selectedSite) } : null
  );

  // 사용할 데이터 결정 (사업장이 선택되면 사업장별 데이터, 아니면 전체 데이터)
  const rawRoleEmpInfo = selectedSite && siteData?.companyInfo ? siteData.companyInfo : roleEmpInfo;
  const currentData = selectedSite && siteData?.gridData ? siteData.gridData : data;
  const rawRoleList = selectedSite && siteData?.commonRoleInfo ? siteData.commonRoleInfo : roleList;
  const currentSupervMappings = selectedSite && siteData?.supervMappings ? siteData.supervMappings : supervMappings;
  const currentSizeCategory = selectedSite && siteData?.sizeCategory ? siteData.sizeCategory : sizeCategory;

  // 안전 직무 필터링 (회사 규모에 따라)
  const { filteredRoleList: currentRoleList, filteredRoleEmpInfo: currentRoleEmpInfo } = filterRoleList(rawRoleList, rawRoleEmpInfo, currentSizeCategory);

  // 역할별 담당자 (동적으로 역할 코드 찾기 - tb_comcd_d 테이블의 실제 데이터 사용)
  const ceoCode = currentRoleList.find(role => role.comdName === "대표 이사")?.comdCode;
  const oshHeadCode = currentRoleList.find(role => role.comdName === "안전보건 관리책임자")?.comdCode;
  const laborRepCode = currentRoleList.find(role => role.comdName === "근로자 대표")?.comdCode;
  const safetyMgrCode = currentRoleList.find(role => role.comdName === "안전관리자")?.comdCode;
  const healthMgrCode = currentRoleList.find(role => role.comdName === "보건관리자")?.comdCode;

  const ceo = currentRoleEmpInfo.find(r => r.role === ceoCode);
  const oshHead = currentRoleEmpInfo.find(r => r.role === oshHeadCode);
  const laborRep = currentRoleEmpInfo.find(r => r.role === laborRepCode);
  const safetyMgrs = currentRoleEmpInfo.filter(r => r.role === safetyMgrCode);
  const healthMgrs = currentRoleEmpInfo.filter(r => r.role === healthMgrCode);

  // 공정설비별 관리감독자 매핑
  const supervMappingsByProcess = useMemo(() => {
    const mappingMap = new Map();

    currentSupervMappings.forEach(mapping => {
      const mpreqCode = mapping.mpreqCode || mapping.MPREQ_CODE;
      if (!mappingMap.has(mpreqCode)) {
        mappingMap.set(mpreqCode, []);
      }
      mappingMap.get(mpreqCode).push({
        empCode: mapping.empCode || mapping.EMP_CODE,
        empName: mapping.empName,
        deptName: mapping.deptName,
        posName: mapping.posName,
        notice: mapping.notice || mapping.NOTICE
      });
    });

    return mappingMap;
  }, [currentSupervMappings]);

  return (
      <div className="official-container">
        {/* 중대재해 현황 이해관계자 */}
        <div className="stakeholder-section">
          <h2 className="section-title">중대재해 현황 이해관계자</h2>

          <div className="og-stakeholder-row">
            {currentRoleList.map((role, index) => {
              let empData = null;
              let empList = [];

              if (role.comdName === "대표 이사") {
                empData = ceo;
              } else if (role.comdName === "안전보건 관리책임자") {
                empData = oshHead;
              } else if (role.comdName === "근로자 대표") {
                empData = laborRep;
              } else if (role.comdName === "안전관리자") {
                empList = safetyMgrs;
              } else if (role.comdName === "보건관리자") {
                empList = healthMgrs;
              } else if (role.comdName === "안전보건 관리담당자") {
                empData = currentRoleEmpInfo.find(emp => emp.role === role.comdCode);
              }

              let iconClass = 'ti-user';
              if (role.comdName === "대표 이사") iconClass = 'ti-building';
              else if (role.comdName === "안전보건 관리책임자") iconClass = 'ti-shield-check';
              else if (role.comdName === "안전관리자") iconClass = 'ti-shield';
              else if (role.comdName === "보건관리자") iconClass = 'ti-heart';
              else if (role.comdName === "근로자 대표") iconClass = 'ti-users';

              let displayName = '-';
              if (empList.length > 0) {
                displayName = empList.length === 1
                    ? empList[0].empName
                    : `${empList[0].empName} 외 ${empList.length - 1}명`;
              } else if (empData) {
                displayName = empData.empName;
              }

              return (
                  <div key={role.comdCode} className="og-stakeholder-card">
                    <div className="og-avatar">
                      <i className={`ti ${iconClass}`}></i>
                    </div>
                    <div className="og-stakeholder-info">
                      <div className="og-stakeholder-label">{role.comdName}</div>
                      <div className="og-stakeholder-name">{displayName}</div>
                    </div>
                  </div>
              );
            })}
          </div>
        </div>

        {/* 단위 공정 */}
        <div className="process-section">
          <h2 className="section-title">단위 공정</h2>

          {/* 사업장이 선택되지 않았을 때 메시지 표시 */}
          {!selectedSite ? (
              <div className="no-site-message">
                <p>사업장을 선택하면 공정별 상세 정보를 확인할 수 있습니다.</p>
              </div>
          ) : (
              <table className="og-table">
                <thead>
                  <tr>
                    <th>공정명</th>
                    <th>설비/장비</th>
                    <th>중대재해 발생형태</th>
                    <th>관리감독자</th>
                    <th>현장관리자</th>
                    <th>실제 작업직원</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((process, index) => {
                    const supervisors = (process.empInfos || []).filter((e) => e.superviser);
                    const fieldMgrs = (process.empInfos || []).filter((e) => e.hasMgr);
                    const workers = (process.empInfos || []).filter((e) => !e.hasMgr && !e.superviser);

                    return (
                        <tr key={`process-${process.mpreqCode}-${process.saccCode || 'no-sacc'}-${index}`}>
                          <td className="og-proc-cell">
                            <span>{process.procName}</span>
                            <span className={`og-badge ${process.saccNames && process.saccNames.length > 0 ? 'og-badge--danger' : 'og-badge--safe'}`}>
                              {process.saccNames && process.saccNames.length > 0 ? '위험' : '안전'}
                            </span>
                          </td>
                          <td style={{ color: 'var(--dash-accent)' }}>{process.eqptName}</td>
                          <td className="og-danger-text">{process.saccNames?.join(', ') || 'N/A'}</td>
                          <td>
                            {supervisors.length > 0 ? (
                                supervisors.map((supervisor, i) => (
                                    <div key={`supervisor-${supervisor.empCode || i}`} className="person-card">
                                      <div className="person-name">{supervisor.empName}</div>
                                      <div className="person-detail">직책 : {supervisor.posName}</div>
                                      <div className="person-detail">부서 : {supervisor.deptName}</div>
                                    </div>
                                ))
                            ) : (
                                <div className="person-card">
                                  <div className="person-name">-</div>
                                  <div className="person-detail">배정된 관리감독자 없음</div>
                                </div>
                            )}
                          </td>
                          <td>
                            {fieldMgrs.length > 0 ? (
                                fieldMgrs.map((manager, i) => (
                                    <div key={`fieldmgr-${manager.empCode || i}`} className="person-card">
                                      <div className="person-name">{manager.empName}</div>
                                      <div className="person-detail">직책 : {manager.posName || '-'}</div>
                                      <div className="person-detail">부서 : {manager.deptName}</div>
                                    </div>
                                ))
                            ) : (
                                <div className="person-card">
                                  <div className="person-name">-</div>
                                  <div className="person-detail">배정된 현장관리자 없음</div>
                                </div>
                            )}
                          </td>
                          <td>
                            {workers.length > 0 ? (
                                workers.map((worker, i) => (
                                    <div key={`worker-${worker.empCode || i}`} className="person-card">
                                      <div className="person-name">{worker.empName}</div>
                                      <div className="person-detail">직책 : {worker.posName || '-'}</div>
                                      <div className="person-detail">부서 : {worker.deptName}</div>
                                    </div>
                                ))
                            ) : (
                                <div className="person-card">
                                  <div className="person-name">-</div>
                                  <div className="person-detail">배정된 작업직원 없음</div>
                                </div>
                            )}
                          </td>
                        </tr>
                    );
                  })}
                </tbody>
              </table>
          )}
        </div>
      </div>
  );
};

export default OfficialGrid;